import { WeightDisplay } from '../weight/WeightDisplay';
import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Tag,
  Text,
  Wrap,
  WrapItem,
  useDisclosure,
} from '@chakra-ui/react';
import {
  Composition,
  CompositionMaterial,
  DimensionValue,
} from '@texas/api/endpoints/compositionApi';
import { CompositionGroupSample } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { compositionGroupApi } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import {
  ProductApprovalState,
  SimpleProductApproval,
} from '@texas/api/endpoints/productApprovalsApi';
import { SampleState as SampleStateEnum } from '@texas/api/endpoints/samplesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { LayoutViewerModal } from '@texas/components/article/variant/versions/shared/LayoutViewerModal';
import { NoData } from '@texas/components/shared/NoData';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { CompositionGroupTitle } from '@texas/components/shared/compositionGroup/CompositionGroupTitle';
import { metricShortName } from '@texas/components/shared/composition/dimension/translations';
import { Icons } from '@texas/components/shared/Icons';
import { ExpandImage } from '@texas/components/shared/image/ExpandImage';
import { ImageLoadingState } from '@texas/components/shared/image/ImageLoadingState';
import { SampleState as SampleStateBadge } from '@texas/components/shared/samples/SampleState';
import { Locale } from '@texas/i18n/types';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  compositionGroupId: number;
}

const emptyValue = '—';

export function CompositionGroupHoverCard({ compositionGroupId }: Props) {
  const { t } = useTranslation();
  const { data, refetch, loading } = useApiResource(
    compositionGroupApi.getGroup,
  );
  const {
    isOpen: isLayoutOpen,
    onOpen: onLayoutOpen,
    onClose: onLayoutClose,
  } = useDisclosure();

  useEffect(() => {
    refetch(compositionGroupId, { includeArchived: true });
  }, [compositionGroupId, refetch]);

  if (loading) return <SpinnerWithLabel label={t('general.loading')} />;
  if (!data) return <NoData label={t('compositionGroup.notExist')} />;

  const activeSamples = (data.productApprovals ?? [])
    .filter(isPendingOrAccepted)
    .filter((pa) => pa.mainSample?.state === SampleStateEnum.Active);
  const compositions = data.compositions.filter((c) => !c.archived);

  return (
    <>
      <Flex
        flexDir="column"
        gap={3}
        minW="360px"
        maxW="min(720px, calc(100vw - 2rem))"
        maxH="70vh"
        overflowY="auto"
      >
        <Flex align="flex-start" justify="space-between" gap={3}>
          <Box>
            <CompositionGroupTitle
              version={data.version}
              option={data.basedOnGroupVersion}
              note={data.note}
              type={data.basedOnGroupVersion == null ? 'option' : 'version'}
              linkedVariantId={data.linkedVariantId}
              fontSize="md"
              fontWeight="semibold"
            />
            <Text fontSize="xs" color="gray.400">
              {data.articleItemCode}
            </Text>
          </Box>
          {data.fileId != null && (
            <Button
              leftIcon={<Icons.Eye boxSize={4} />}
              size="xs"
              variant="texas-light"
              onClick={(event) => {
                event.stopPropagation();
                onLayoutOpen();
              }}
            >
              {t('compositionGroup.version.viewLayout')}
            </Button>
          )}
        </Flex>

        <SampleSection activeSamples={activeSamples} />

        <Divider />

        <Flex direction="column" gap={3}>
          <Heading fontSize="sm">{t('composition.compositions')}</Heading>
          {compositions.length === 0 ? (
            <Text fontSize="sm" color="gray.500">
              {t('composition.noComposition')}
            </Text>
          ) : (
            compositions.map((composition, index) => (
              <CompositionMetadata
                key={composition.id}
                composition={composition}
                index={index}
              />
            ))
          )}
        </Flex>
      </Flex>

      {data.fileId != null && (
        <LayoutViewerModal
          isOpen={isLayoutOpen}
          onClose={onLayoutClose}
          fileId={data.fileId}
        />
      )}
    </>
  );
}

function SampleSection({
  activeSamples,
}: {
  activeSamples: SimpleProductApproval[];
}) {
  const { t } = useTranslation();

  return (
    <Flex direction="column" gap={2}>
      <Heading fontSize="sm">{t('sample.samples')}</Heading>
      {activeSamples.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          {t('garp.noSample')}
        </Text>
      ) : (
        activeSamples.map((productApproval) => (
          <SampleMetadata
            key={productApproval.productApprovalId}
            sample={productApproval.mainSample!}
            paNumber={productApproval.formatNumber || productApproval.number}
            imageIdentifier={productApproval.fileIdentifier}
          />
        ))
      )}
    </Flex>
  );
}

function SampleMetadata({
  sample,
  paNumber,
  imageIdentifier,
}: {
  sample: CompositionGroupSample;
  paNumber: string;
  imageIdentifier: string | null;
}) {
  const { t } = useTranslation();

  return (
    <Box borderWidth="1px" borderColor="whiteAlpha.200" borderRadius="md" p={2}>
      <Flex align="flex-start" gap={3}>
        <PreviewImage
          identifier={imageIdentifier}
          label={t('sample.sample')}
          emptyLabel={t('sample.imageMissing')}
        />
        <Flex direction="column" gap={1} minW={0} flex={1}>
          <Text fontWeight="semibold">{paNumber}</Text>
          <Flex align="center" gap={2} wrap="wrap">
            <SampleStateBadge
              lifecycle={sample.lifecycle}
              sampleReadyDate={sample.sampleReadyDate}
            />
            <Text fontSize="xs" color="gray.400">
              {sample.lifecycle}
            </Text>
          </Flex>
          <Text fontSize="xs" color="gray.400">
            {t('article.sampleReadyDate')}:{' '}
            {sample.sampleReadyDate
              ? formatDate(Locale.En, sample.sampleReadyDate)
              : emptyValue}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
}

function CompositionMetadata({
  composition,
  index,
}: {
  composition: Composition;
  index: number;
}) {
  const { t } = useTranslation();

  return (
    <Box borderWidth="1px" borderColor="whiteAlpha.200" borderRadius="md" p={2}>
      <Flex justify="space-between" align="center" mb={2} gap={2}>
        <Heading fontSize="sm">
          {t('composition.compositionNr', { nr: index + 1 })}
        </Heading>
        <Tag size="sm">
          <WeightDisplay value={composition.weight} />
        </Tag>
      </Flex>

      <Flex gap={3} align="flex-start" mb={3}>
        <PreviewImage
          identifier={composition.fileIdentifier}
          label={t('fileBrowser.image')}
          emptyLabel={t('sample.imageMissing')}
        />
        <Flex direction="column" gap={2} minW={0} flex={1}>
          {composition.dimensionGroup && (
            <DimensionGroupMetadata composition={composition} />
          )}
          {composition.note && (
            <Text fontSize="xs" color="gray.400">
              {composition.note}
            </Text>
          )}
        </Flex>
      </Flex>

      {composition.materials.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          {t('compositionGroup.hover.noMaterials')}
        </Text>
      ) : (
        <Flex direction="column" gap={2}>
          {composition.materials.map((material) => (
            <MaterialMetadata key={material.id} material={material} />
          ))}
        </Flex>
      )}
    </Box>
  );
}

function DimensionGroupMetadata({ composition }: { composition: Composition }) {
  const { t } = useTranslation();
  const dimensionGroup = composition.dimensionGroup;

  if (!dimensionGroup) return null;

  return (
    <Flex direction="column" gap={2}>
      <Flex gap={3} align="flex-start">
        <PreviewImage
          identifier={dimensionGroup.visualFileIdentifier ?? null}
          label={t('compositionGroup.hover.dimensionGroup')}
          emptyLabel={emptyValue}
        />
        <Box minW={0}>
          <Text fontWeight="semibold">{dimensionGroup.name}</Text>
          {dimensionGroup.description && (
            <Text fontSize="xs" color="gray.400">
              {dimensionGroup.description}
            </Text>
          )}
        </Box>
      </Flex>
      {dimensionGroup.fileIdentifier && (
        <PreviewImage
          identifier={dimensionGroup.fileIdentifier}
          label={t('configuration.dimensions')}
          emptyLabel={emptyValue}
          wide={true}
        />
      )}
      {dimensionGroup.dimensions.length > 0 && (
        <DimensionList dimensions={dimensionGroup.dimensions} />
      )}
    </Flex>
  );
}

function DimensionList({ dimensions }: { dimensions: DimensionValue[] }) {
  return (
    <Flex direction="column" gap={1}>
      {dimensions.map((dimension) => (
        <Flex key={dimension.id} justify="space-between" gap={3}>
          <Text fontSize="xs" textAlign="left">
            {dimension.identifier
              ? `${dimension.identifier}: ${dimension.label}`
              : dimension.label}
          </Text>
          <Text fontSize="xs" whiteSpace="nowrap">
            {dimension.value} {metricShortName(dimension.metric)}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
}

function MaterialMetadata({ material }: { material: CompositionMaterial }) {
  const { t } = useTranslation();

  return (
    <Box borderWidth="1px" borderColor="whiteAlpha.100" borderRadius="md" p={2}>
      <Flex justify="space-between" align="center" gap={2} mb={2}>
        <Box minW={0}>
          <Text fontWeight="semibold">{material.material.text}</Text>
          <Text fontSize="xs" color="gray.400">
            {material.material.code}
          </Text>
        </Box>
        <Tag size="sm">
          <WeightDisplay value={material.weight} />
        </Tag>
      </Flex>

      <Flex direction="column" gap={2}>
        <ValueRow
          label={t('compositionGroup.hover.materialArticleText')}
          value={material.material.articleTextField}
        />
        <ValueRow
          label={t('compositionGroup.hover.certificationText')}
          value={material.material.certificationText}
        />
        <ValueRow
          label={t('compositionGroup.hover.qualities')}
          values={material.qualities.map((q) =>
            q.value ? `${q.text}: ${q.value}` : q.text,
          )}
        />
        <ValueRow
          label={t('compositionGroup.hover.techniques')}
          values={material.techniques.map((technique) => technique.text)}
        />
        <ValueRow
          label={t('compositionGroup.hover.treatments')}
          values={material.treatments.map((treatment) => treatment.text)}
        />
        <ValueRow
          label={t('general.colors')}
          values={material.colors.map((color) => color.name)}
        />
        {material.note && (
          <ValueRow label={t('general.note')} value={material.note} />
        )}
      </Flex>
    </Box>
  );
}

function ValueRow({
  label,
  value,
  values,
}: {
  label: string;
  value?: string | null;
  values?: string[];
}) {
  const filtered = values?.filter(Boolean) ?? [];

  return (
    <Flex justify="space-between" gap={3} align="flex-start">
      <Text fontSize="xs" color="gray.400">
        {label}
      </Text>
      {filtered.length > 0 ? (
        <TagList values={filtered} />
      ) : (
        <Text fontSize="xs" textAlign="right">
          {value ?? emptyValue}
        </Text>
      )}
    </Flex>
  );
}

function TagList({ values }: { values: string[] }) {
  return (
    <Wrap justify="flex-end" spacing={1}>
      {values.map((value) => (
        <WrapItem key={value}>
          <Tag size="sm">{value}</Tag>
        </WrapItem>
      ))}
    </Wrap>
  );
}

function PreviewImage({
  identifier,
  label,
  emptyLabel,
  wide = false,
}: {
  identifier: string | null;
  label: string;
  emptyLabel: string;
  wide?: boolean;
}) {
  if (!identifier) {
    return (
      <Flex
        align="center"
        justify="center"
        borderWidth="1px"
        borderColor="whiteAlpha.200"
        borderRadius="md"
        color="gray.500"
        fontSize="xs"
        minW={wide ? '100%' : '96px'}
        h={wide ? '120px' : '96px'}
      >
        {emptyLabel}
      </Flex>
    );
  }

  return (
    <Box w={wide ? '100%' : '96px'} flexShrink={0}>
      <Text fontSize="xs" color="gray.400" mb={1}>
        {label}
      </Text>
      <ExpandImage variant="scale" identifier={identifier}>
        <ImageLoadingState
          identifier={identifier}
          size={256}
          borderRadius="md"
          borderWidth="1px"
          borderColor="whiteAlpha.200"
          overflow="hidden"
          w="100%"
          h={wide ? '120px' : '96px'}
          sx={{
            img: {
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            },
          }}
        />
      </ExpandImage>
    </Box>
  );
}

function isPendingOrAccepted(productApproval: { state: ProductApprovalState }) {
  return (
    productApproval.state === ProductApprovalState.Pending ||
    productApproval.state === ProductApprovalState.Accepted
  );
}
